import type { AuthStore, AuthUser } from "@/context/auth";
import type { Product } from "@workspace/api-client-react";
import type { OfflineSale } from "./offline-sales";
import { effectiveSubscriptionEnd } from "./subscription-time.ts";

export const OFFLINE_WORKSPACE_EVENT = "retailpulse:offline-workspace";
export const OFFLINE_ACCESS_MS = 12 * 60 * 60 * 1000;
export type OfflineIdentity = { user: AuthUser; store: AuthStore; verifiedAt: number; expiresAt: number };
export type CatalogProduct = Pick<Product, "id" | "name" | "sku" | "price" | "stock" | "reorderLevel" | "unit" | "brand" | "categoryName" | "description" | "isService" | "vatApplicable" | "hasVariants" | "variants" | "branchId">;
export type OfflineCatalog = {
  savedAt: number;
  products: CatalogProduct[];
  customers: { id: number; name: string; discountRate?: number; groupDiscountValue?: number; groupDiscountType?: string | null }[];
  staff: { id: number; name: string; isActive: boolean }[];
};

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("retailpulse-offline-workspace", 1);
    request.onupgradeneeded = () => request.result.createObjectStore("records");
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Device storage is unavailable"));
    request.onblocked = () => reject(new Error("Close other RetailPulse tabs and retry offline preparation"));
  });
}

export async function workspaceRecord<T>(key: string, value?: T): Promise<T | undefined> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("records", value === undefined ? "readonly" : "readwrite");
    const request = value === undefined ? tx.objectStore("records").get(key) : tx.objectStore("records").put(value, key);
    let result: T | undefined;
    request.onsuccess = () => { result = value === undefined ? request.result : value; };
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = tx.onabort = () => { db.close(); reject(tx.error || new Error("Device storage failed")); };
  });
}

export async function clearOfflineWorkspace(): Promise<void> {
  const db = await database();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("records", "readwrite");
    tx.objectStore("records").clear();
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = tx.onabort = () => { db.close(); reject(tx.error || new Error("Could not clear offline access")); };
  });
  window.dispatchEvent(new Event(OFFLINE_WORKSPACE_EVENT));
}

export function validOfflineIdentity(identity: OfflineIdentity | undefined, now = Date.now()): identity is OfflineIdentity {
  return Boolean(identity && Number.isFinite(identity.verifiedAt) && Number.isFinite(identity.expiresAt)
    && identity.verifiedAt <= now && now < identity.expiresAt
    && now - identity.verifiedAt < OFFLINE_ACCESS_MS && identity.store.isActive
    && effectiveSubscriptionEnd(identity.store.trialEndsAt).getTime() > now
    && ["active", "trial"].includes(identity.store.subscriptionStatus)
    && identity.user.role !== "superadmin"
    && (identity.user.role === "admin" || identity.user.permissions?.includes("sales")));
}

export function offlineIdentity(user: AuthUser, store: AuthStore, now = Date.now()): OfflineIdentity {
  // This is a local workspace hint, NEVER a server session or an authorization token.
  return {
    user: { id: user.id, name: user.name, email: "", role: user.role, businessType: user.businessType, permissions: user.permissions },
    store: { ...store },
    verifiedAt: now,
    expiresAt: Math.min(now + OFFLINE_ACCESS_MS, effectiveSubscriptionEnd(store.trialEndsAt).getTime()),
  };
}

export const catalogKey = (storeId: number, userId: number, branchId: number | null) =>
  `catalog:${storeId}:${userId}:${branchId ?? "main"}`;

export function cacheProducts(products: Product[]): CatalogProduct[] {
  return products.map(({ id, name, sku, price, stock, reorderLevel, unit, brand, categoryName, description, isService, vatApplicable, hasVariants, variants, branchId }) =>
    ({ id, name, sku, price, stock, reorderLevel, unit, brand, categoryName, description, isService, vatApplicable, hasVariants, variants, branchId }));
}

export function stockAfterQueuedSales<T extends CatalogProduct>(products: T[], sales: OfflineSale[], branchId: number | null): T[] {
  const deductions = new Map<string, number>();
  for (const sale of sales) {
    if ((sale.body.branchId ?? null) !== branchId) continue;
    for (const item of (sale.body.items ?? []) as { productId: number; variantId?: string; quantity: number }[]) {
      const key = `${item.productId}:${item.variantId ?? ""}`;
      deductions.set(key, (deductions.get(key) ?? 0) + Number(item.quantity));
    }
  }
  return products.map(product => {
    if (product.isService) return product;
    let variants = product.variants;
    let deduction = deductions.get(`${product.id}:`) ?? 0;
    if (product.hasVariants && variants) {
      try {
        const rows = JSON.parse(variants);
        if (Array.isArray(rows)) {
          variants = JSON.stringify(rows.map(row => {
            const amount = deductions.get(`${product.id}:${row.id}`) ?? 0;
            deduction += amount;
            return { ...row, stock: Math.max(0, Number(row.stock) - amount) };
          }));
        }
      } catch { /* Preserve invalid variant data so the existing picker rejects it. */ }
    }
    return { ...product, stock: Math.max(0, product.stock - deduction), variants };
  });
}
